'use client';

import {
  ApplicationNotificationActionRow,
  EntityIcon,
  HeadingItem,
  getStatusIcon,
} from '@terra/ui-design-system';
import { Alert01Icon, ChatBotIcon, Key01Icon } from '@terra/ui-icons';
import { Box, token } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useNotificationActionsForHome } from '../../hooks/dashboard/useNotificationActionsForHome';
import { useTargetType } from '../../store/projectStore';
import { formatNumber } from '../../utils/formatNumber';
import {
  DASHBOARD_ACTION_LIST_HEIGHT,
  DashboardActionList,
} from './DashboardActionList';
import { DashboardActionRow } from './DashboardActionRow';
import { DashboardCard } from './DashboardCard';
import type {
  NeedsAttentionItem,
  NeedsAttentionKey,
} from './needs-attention-items';
import { useOpenHomeDestination } from './useOpenHomeDestination';

const ITEM_ICON: Record<NeedsAttentionKey, ReactNode> = {
  failedCredentials: (
    <Key01Icon size={16} color={token('colors.fg.neutral.error')} />
  ),
  waitingForInput: (
    <ChatBotIcon size={16} color={token('colors.bg.graphs.yellow')} />
  ),
  blockedExecutions: getStatusIcon('BLOCKED', 16),
  pausedExecutions: getStatusIcon('PAUSED', 16),
  blockedSignals: getStatusIcon('BLOCKED', 16),
  signalsNeedingReview: (
    <EntityIcon type="signal" appearance="highlighted" density="compact" />
  ),
  unpublishedFindings: (
    <EntityIcon type="finding" appearance="highlighted" density="compact" />
  ),
  failedExecutions: getStatusIcon('FAILED', 16),
  criticalFindings: (
    <Alert01Icon size={16} color={token('colors.bg.graphs.critical')} />
  ),
  lastScanFailed: getStatusIcon('FAILED', 16),
  discoveryFailed: getStatusIcon('FAILED', 16),
};

type NeedsAttentionCardProps = {
  applicationId: string;
  items: NeedsAttentionItem[];
  isLoading: boolean;
};

export function NeedsAttentionCard({
  applicationId,
  items,
  isLoading,
}: NeedsAttentionCardProps) {
  const t = useTranslations('home.needsAttention');
  const targetType = useTargetType();
  const {
    actions,
    isLoading: isActionsLoading,
    openAction,
    dismissAction,
  } = useNotificationActionsForHome({ applicationId, targetType });
  const { openDestination, openDestinationContextMenu } =
    useOpenHomeDestination({ applicationId });
  const isCardLoading = isLoading || isActionsLoading;

  return (
    <DashboardCard
      title={t('title')}
      isLoading={isCardLoading}
      isEmpty={!isCardLoading && items.length === 0 && actions.length === 0}
      emptyLabel={t('empty')}
      contentHeight={DASHBOARD_ACTION_LIST_HEIGHT}
      h="full"
    >
      <DashboardActionList>
        {items.map((item, index) => (
          <DashboardActionRow
            key={item.key}
            icon={ITEM_ICON[item.key]}
            title={t(`items.${item.key}.title`)}
            description={t(`items.${item.key}.description`)}
            trailingContent={
              <HeadingItem color="fg.neutral.default">
                {formatNumber(item.count)}
              </HeadingItem>
            }
            showDivider={index > 0}
            onClick={() => openDestination(item.target)}
            onContextMenu={(event) =>
              openDestinationContextMenu(event, item.target)
            }
          />
        ))}
        {actions.map((action, index) => (
          <Box
            key={action.id}
            px={1}
            borderTopWidth={items.length + index > 0 ? '1px' : 0}
            borderTopStyle="solid"
            borderTopColor="border.neutral.subtle"
          >
            <ApplicationNotificationActionRow
              action={action}
              showBottomBorder={false}
              onPrimaryAction={({ actionId }) => openAction(actionId)}
              onDismiss={({ actionId }) => dismissAction(actionId)}
            />
          </Box>
        ))}
      </DashboardActionList>
    </DashboardCard>
  );
}
